import { useCallback, useEffect, useState } from 'react';
import { useTestMode } from '../lib/testModeContext.jsx';

const LS_KEY = 'somesay_last_coords';

// Light GPS hook with three resolution paths:
//   1. Test mode mockLocation (instant, no permission)
//   2. navigator.geolocation (real GPS, best-effort)
//   3. last-known coords from localStorage (offline fallback so the
//      Photo Coaching tab still scores scenarios on a cell-dead boat
//      deck). Stored every successful resolve, age included.
//
// Returns { coords, status, error, refresh }. coords is
// { lat, lng, accuracy?, when, source: 'mock'|'gps'|'cached' } or null.
export function useCoords({ enableHighAccuracy = false, maximumAge = 60000, timeout = 8000 } = {}) {
  const testMode = useTestMode();
  const [coords, setCoords] = useState(null);
  const [status, setStatus] = useState('idle'); // 'idle' | 'loading' | 'ready' | 'denied' | 'unavailable' | 'cached'
  const [error, setError] = useState(null);

  const persist = useCallback((c) => {
    if (!c) return;
    try {
      window.localStorage.setItem(LS_KEY, JSON.stringify(c));
    } catch {
      /* quota or private mode — silently ignore */
    }
  }, []);

  const readCached = useCallback(() => {
    try {
      const raw = window.localStorage.getItem(LS_KEY);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      if (parsed?.lat == null || parsed?.lng == null) return null;
      return { ...parsed, source: 'cached' };
    } catch {
      return null;
    }
  }, []);

  const refresh = useCallback(() => {
    // Test mode short-circuits to the configured mock — no permission
    // prompt, no real GPS hit. Mirrors the SOS tab's pattern.
    if (testMode?.active && testMode.mockLocation) {
      const c = {
        lat: testMode.mockLocation.lat,
        lng: testMode.mockLocation.lng,
        when: new Date().toISOString(),
        source: 'mock',
        label: testMode.mockLocation.label || null,
      };
      setCoords(c);
      setStatus('ready');
      setError(null);
      persist(c);
      return;
    }

    if (typeof navigator === 'undefined' || !navigator.geolocation) {
      // Surface last-known so the tab still functions offline.
      const cached = readCached();
      if (cached) {
        setCoords(cached);
        setStatus('cached');
      } else {
        setStatus('unavailable');
      }
      return;
    }

    setStatus('loading');
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const c = {
          lat: pos.coords.latitude,
          lng: pos.coords.longitude,
          accuracy: pos.coords.accuracy,
          when: new Date(pos.timestamp).toISOString(),
          source: 'gps',
        };
        setCoords(c);
        setStatus('ready');
        setError(null);
        persist(c);
      },
      (err) => {
        // Permission denied, position unavailable, timeout — fall back
        // to last-known so scoring still works.
        const cached = readCached();
        if (cached) {
          setCoords(cached);
          setStatus('cached');
        } else {
          setStatus(err.code === 1 ? 'denied' : 'unavailable');
        }
        setError(err.message || 'Could not read location.');
      },
      { enableHighAccuracy, timeout, maximumAge }
    );
  }, [testMode?.active, testMode?.mockLocation, persist, readCached, enableHighAccuracy, timeout, maximumAge]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  return { coords, status, error, refresh };
}
